import { Component, EventEmitter, Input, NgZone, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormGroup, NgControl } from '@angular/forms';
import { BehaviorSubject, defer, filter, firstValueFrom, map, Subject, takeUntil, timeout } from 'rxjs';
import FotosClass from 'src/app/model/api/datos/fotosClass';
import { IonicComponentsModule } from 'src/app/modules/ionic-components.module';
import { FotoService } from 'src/app/services/foto.service';
import { TakePhotoComponent } from '../take-photo/take-photo.component';
import { AlertService } from 'src/app/services/alert.service';
import { LoaderService } from 'src/app/services/loader.service';
import { MainService } from 'src/app/api/main.service';
import { DatabaseService } from 'src/app/database/database.service';
import { UtilService } from 'src/app/services/util.service';
import { CanvasDrawingComponent } from '../canvas-drawing/canvas-drawing.component';
import { QRCodeModule } from 'angularx-qrcode';
import { RemoveFileExtensionPipe } from 'src/app/pipes/remove-file-extension.pipe';
import { GpsService } from 'src/app/services/gps.service';

@Component({
  selector: 'app-fotos',
  templateUrl: './fotos.component.html',
  styleUrls: ['./fotos.component.scss'],
  standalone: true,
  imports: [
    IonicComponentsModule,
    TakePhotoComponent,
    CanvasDrawingComponent,
    QRCodeModule,
  ]
})
export class FotosComponent implements ControlValueAccessor {

  //#region Propiedades

  @Input("fotosSubject") public FotosSubject: BehaviorSubject<FotosClass[]>
  @Input("categoria") public Categoria: string = null
  @Input("form") public form: FormGroup<any>
  @ViewChild("photo") public photo: TakePhotoComponent
  private _value: string
  public get value(): string {
    return this._value
  }
  @Input("value") public set value(val: string) {
    this._value = val
  }
  @Output("valueChange") public valueChange = new EventEmitter<string>()
  @Output("valueSelected") public valueSelected = new EventEmitter<{newValue: string, oldValue: string}>()
  @Input("disallow-fotos-without-gps") public DisallowPhotosWithoutGPSMetadata = false
  @Input("readonly") public readonly: boolean = false

  //#endregion

  //#region Constructor

  constructor(
    private fotoService: FotoService,
    private alertService: AlertService,
    private zone: NgZone,
    private loader: LoaderService,
    private service: MainService,
    private database: DatabaseService,
    private util: UtilService,
    private gps: GpsService,
    @Self() @Optional() private control: NgControl
  ) { 
    if (this.control) this.control.valueAccessor = this
  }

  public async ngOnInit(): Promise<void> {
    this.FotosSubject.pipe(
      takeUntil(this.destroy$),
      map(p => {
        if (this.Categoria == null) return p.filter(q => q.Categoria == null)
        return p.filter(q => q.Categoria == this.Categoria)
      })
    ).subscribe(p => {
      let oldValue = this.value
      if (p.length == 0) {
        this.value = ""
      } else {
        this.value = p.length.toString()
      }
      if (oldValue != this.value) {
        this.onChange(this.value)
        this.valueChange.emit(this.value)
        this.valueSelected.emit({
          newValue: this.value,
          oldValue: oldValue
        })
      }
    })
  }

  private destroy$ = new Subject<void>()
  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  //#endregion

  //#region QR

  public getURLQRFotos(): string {
    return `${location.protocol}//${location.host}/subir-fotos-inspeccion?tkn=${this.form.get('UploadFotosToken')?.value}&categoria=${encodeURIComponent(this.Categoria || '')}${this.DisallowPhotosWithoutGPSMetadata ? '&forceGPS=true' : ''}`
  }

  //#endregion

  //#region Sincronización de fotos

  public async sincronizarFotos(): Promise<void> {
    try {
      await this.loader.show('Sincronizando imágenes...')
      let fotosLocales = await firstValueFrom(this.FotosSubject)
      let fotosInspeccion = await this.service.Inspecciones.getImagenes(this.form.get('Id')?.value)
      let fotosNuevas = fotosInspeccion.filter(p => fotosLocales.find(q => q.Id == p.Id) == null)
      for (let fotoNueva of fotosNuevas) {
        await this.loader.hide()
        await this.loader.show(`Descargando imagen ${fotosNuevas.indexOf(fotoNueva) + 1} de ${fotosNuevas.length}`)
        let imagenLocal = new FotosClass()
        imagenLocal.Id = fotoNueva.Id
        imagenLocal.FileName = fotoNueva.FileName
        imagenLocal.OrdenLocalId = this.form.get('LocalId')?.value
        imagenLocal.SHA256 = fotoNueva.SHA256
        imagenLocal.Categoria = fotoNueva.Categoria != null && fotoNueva.Categoria != '' ? fotoNueva.Categoria : null
        imagenLocal.FotoData = await this.service.Inspecciones.downloadImagen(this.form.get('Id')?.value, fotoNueva.Id)
        fotosLocales.push(imagenLocal)

        await this.database.Fotos.update(imagenLocal)
      }
      this.FotosSubject.next(fotosLocales)
      await this.loader.hide()
      await this.loader.show('Actualizando...')
      await this.service.Inspecciones.changesubidadesdemovilimagenes(this.form.get('Id')?.value)
      await this.loader.hide()
    } catch (ex) {
      await this.loader.hideAll()
      await this.alertService.showMessage(`No se ha podido sincronizar las imágenes: ${ex?.message || 'Error desconocido.'}`)
    }
  }

  //#endregion  

  //#region Fotos

  public async AdjuntarArchivo(): Promise<void> {
    if (this.readonly) return
    this.fotoService.AdjuntarArchivo(this.form.get('LocalId')?.value, this.Categoria, this.DisallowPhotosWithoutGPSMetadata)
  }

  public async EliminarFoto(foto: FotosClass): Promise<void> {
    if (this.readonly) return
    if (await this.alertService.showConfirm('¿Quieres eliminar la foto seleccionada?', 'Eliminar foto', 'Sí, eliminar', 'No, cancelar')) {
      let fotos = await firstValueFrom(this.FotosSubject)
      fotos = fotos.filter(p => p != foto)
      this.zone.run(async () => {
        this.FotosSubject.next(fotos)
      })
    }
  }
  
  public async obtenerInformacionImagen(img: HTMLImageElement): Promise<void> {
    let metaData = await this.fotoService.obtenerInformacionImagen(img)
    this.alertService.showMessage(JSON.stringify(metaData))
  }

  public async updateFoto(foto: FotosClass, base64: string): Promise<void> {
    let base64Data = base64.substring(base64.indexOf(',') + 1)
    foto.SHA256 = await this.util.SHA256FromBase64Data(base64Data)
    foto.FotoData = base64
    foto.Categoria = this.Categoria
  }

  public async AddFotoCamara(data: {data: string, hash: string}): Promise<void> {
    if (this.DisallowPhotosWithoutGPSMetadata) {
      // Reobtenemos las coordenadas, ya que las vamos a guardar actualizadas.
      try {
        await this.loader.show(`Obteniendo información GPS...`)
        let coords = await firstValueFrom(defer(() => this.gps.getGpsCoordinates({
          enableHighAccuracy: true,
          timeout: 10000
        })).pipe(
          timeout(10000)
        ))
        await this.loader.hide()
        if (coords == null || coords.coords == null) {
          this.alertService.showMessage(`Para poder subir la foto se requiere la ubicación GPS. No se ha podido obtener información de GPS.`)
          return
        }
        if (coords.coords.accuracy > 100) {
          this.alertService.showMessage(`La precisión del dispositivo GPS es demasiado baja para poder adjuntar una foto con ubicación GPS.`)
          return
        }
        // Todo correcto, insertamos la foto y le añadimos la información GPS.
        data.data = await this.fotoService.escribirMetadatosGPS(data.data, coords)
        let fotoBase64 = data.data.substring(data.data.indexOf(',') + 1)
        data.hash = await this.util.SHA256FromBase64Data(fotoBase64)

        this.fotoService.AddFotoCamara(data, this.form.get('LocalId')?.value, this.Categoria)
      } catch (ex) {
        this.loader.hide()
        this.alertService.showMessage(`Para poder subir la foto se requiere la ubicación GPS. No se ha podido obtener información de GPS.`)
      }
    } else {
      this.fotoService.AddFotoCamara(data, this.form.get('LocalId')?.value, this.Categoria)
    }
  }

  public HayFotos(fotos: FotosClass[]): boolean {
    if (this.Categoria == null) {
      return fotos != null && fotos.filter(p => p.Categoria == null).length > 0
    } else {
      return fotos != null && fotos.filter(p => p.Categoria == this.Categoria).length > 0
    }
  }

  public async renameFoto(foto: FotosClass): Promise<void> {
    let removeFileExtension = new RemoveFileExtensionPipe()
    let fileName = removeFileExtension.transform(foto.FileName)
    let extension = this.util.GetFileExtension(foto.FileName)

    let newFileName = await this.alertService.showPrompt(`Escribe un nuevo nombre para la foto`, 'Renombrar foto', fileName, 'Renombrar', 'Cancelar', fileName)
    if (newFileName != null && newFileName != '') {
      let fotos = await firstValueFrom(this.FotosSubject)
      foto.FileName = `${newFileName}${extension}`
      this.FotosSubject.next(fotos)
    }

  }

  public async HacerFoto(): Promise<void> {
    if (this.readonly) return
    if (this.DisallowPhotosWithoutGPSMetadata) {
      try {
        await this.loader.show(`Verificando información GPS...`)
        let coords = await firstValueFrom(defer(() => this.gps.getGpsCoordinates({
          enableHighAccuracy: true,
          timeout: 10000
        })).pipe(
          timeout(10000)
        ))
        await this.loader.hide()
        if (coords == null || coords.coords == null) {
          this.alertService.showMessage(`Para poder hacer una foto con ubicación GPS desde este dispositivo es necesario tener habilitada la ubicación.`)
          return
        }
        if (coords.coords.accuracy > 100) {
          this.alertService.showMessage(`La precisión del dispositivo GPS es demasiado baja para poder adjuntar una foto con ubicación GPS.`)
          return
        }
        // Todo correcto, lanzamos el componente de la foto.
        this.photo.show()
      } catch (ex) {
        this.loader.hide()
        this.alertService.showMessage(`Para poder hacer una foto con ubicación GPS desde este dispositivo es necesario tener habilitada la ubicación.`)
      }
    } else {
      this.photo.show()
    }
  }

  //#endregion

  //#region Reactive Forms

  private onChange = (value: string) => {}
  private onTouched = () => {}

  writeValue(value: string): void {
    this.value = value
    this.valueChange.emit(value)
    this.onChange(value)
  }

  registerOnChange(fn: (value: string) => {}): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn
  }

  public isInvalid(): boolean {
    return this.control ? this.control.invalid : false
  }

  public isTouched(): boolean {
    return this.control ? this.control.touched : false
  }

  //#endregion
  

}
